/**
=========================================================
* Material Kit 2 React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { Suspense, lazy, useEffect, useState } from "react";

// react-router components
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";

// @mui material components
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";

// Material Kit 2 React themes
import { CircularProgress } from "@mui/material";
import theme from "assets/theme";
import toastACI from "components/toastACI";
import useStorage from "hooks/useStorage";
import SebValidationWrapper from "layouts/components/SebValidationWrapper";
import { handleCurrentSubtestOnLogin } from "libs/authFunction";
import client from "libs/client";
import commonHelper from "libs/commonHelper";
import { autoSaveAnswer } from "libs/examFunction";
import security from "libs/security";
import moment from "moment";
import "moment-timezone";
import { useIdleTimer } from "react-idle-timer";
import { useDispatch } from "react-redux";
import { onLogout } from "redux/actions/loginForm";
import { CustomProvider, useToaster } from "rsuite";

const DownloadSeb = lazy(() => import("pages/SebPages/DownloadSeb"));
const SignInBasic = lazy(() => import("pages/LandingPages/SignIn"));
const TermsConditions = lazy(() => import("pages/PrivacyPolicyTermsConditions/TermsConditions"));
const PrivacyPolicy = lazy(() => import("pages/PrivacyPolicyTermsConditions/PrivacyPolicy"));
const QuestLayoutExam = lazy(() => import("pages/QuestLayoutExam"));
const InstructionExam = lazy(() => import("pages/InstructionExam"));
const FindingPartner = lazy(() => import("pages/FindingPartner"));
const ProctoringExam = lazy(() => import("pages/ProctoringExam"));
const ProctoringAmanin = lazy(() => import("pages/ProctoringAmanin"));
const DashboardExam = lazy(() => import("pages/DashboardExam"));
const FinishExam = lazy(() => import("pages/FinishExam"));

export default function App() {
  // init custom history object to allow navigation from
  // anywhere in the react app (inside or outside components)
  moment.tz.setDefault("Asia/Jakarta");
  commonHelper.history.navigate = useNavigate();
  commonHelper.history.location = useLocation();
  commonHelper.toast = useToaster();
  const queryParams = new URLSearchParams(window.location.search);
  const accessToken = queryParams.get(process.env.REACT_APP_ACCESS_TOKEN_KEY);
  const isIframe = commonHelper.isIframe();

  const { getItem, setItem, removeItem, clear } = useStorage();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const toast = useToaster();
  const [isLogin, setIsLogin] = useState(getItem("isLogin"));

  const participant = getItem("participant");

  const handleLocalStorageOnLogout = () => {
    const onInstruction = getItem("savedTimerInstruction");
    const onExam = getItem("exam");
    const isStuckInstruction = getItem("isStuckInstruction");

    if ((onInstruction || onExam) && !isStuckInstruction) {
      setItem("lastParticipant", participant);
      removeItem("_grecaptcha");
      removeItem("isLogin");
      removeItem("token");
    } else {
      clear();
    }
  };

  if (isLogin && accessToken && isIframe) {
    handleLocalStorageOnLogout();
    dispatch(onLogout());
    setIsLogin(false);
  }

  useIdleTimer({
    timeout: 1000 * 60 * 60 * 24,
    onIdle: () => {
      if (isLogin) {
        removeItem("isLogin");
        setIsLogin(false);
      }
    },
    debounce: 500,
  });

  const handleSessionIframe = async () => {
    const token = accessToken.replaceAll(" ", "+");
    const ipAddress = "";
    const isContinue = false;
    const tokenData = security.decryptData(token);
    const req = {
      method: "POST",
      endpoint: "api/v1/authLoginParticipant",
      data: {
        iframe_token: tokenData.iframeToken,
      },
      isAuth: true,
      isToken: true,
      pToken: tokenData.bearerToken,
    };
    const res = await client.requestAPI(req);

    handleCurrentSubtestOnLogin(res, isContinue, ipAddress);
  };

  const checkLoginTokenExpiry = async () => {
    const res = await client.requestAPI({
      endpoint: "api/v1/hello",
      isAuth: true,
    });
    if (res?.message === "Hello World Server") {
      // eslint-disable-next-line
      console.log("Token status: Active");
    } else {
      // eslint-disable-next-line
      console.error("Error check token status");
    }
  };

  useEffect(() => {
    // Setting page scroll to 0 when changing the route
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;

    if (
      pathname !== "/login" &&
      !pathname.includes("Seb") &&
      pathname !== "/privacyPolicy" &&
      pathname !== "/termsConditions"
    ) {
      if (accessToken && isIframe) {
        handleSessionIframe();
      } else {
        checkLoginTokenExpiry();
      }
    }
  }, [pathname]);

  const handleLogout = async (locFunction = "Logout") => {
    const par = participant ?? getItem("participant");
    const res = await client.requestAPI({
      endpoint: "api/v1/logout",
      method: "POST",
      isAuth: true,
      headers: {
        "X-Encrypted-IP": par?.ipAddress,
      },
      data: {
        projectId: par?.projectId,
        participantId: par?.participantId,
        locFunction,
      },
    });
    if (res?.meta?.status === "Success") {
      handleLocalStorageOnLogout();
      dispatch(onLogout());
      commonHelper.navigate("/login");

      toast.push(
        toastACI({
          type: "success",
          header: "Log Out Success",
          message: `You have successfully Logged Out!`,
        }),
        {
          placement: "topEnd",
        }
      );
    } else {
      toast.push(
        toastACI({
          type: "error",
          header: "Log Out Failed",
          message: res?.error?.message || `Something wrong, failed to logout!`,
        }),
        {
          placement: "topEnd",
        }
      );
    }
  };

  useEffect(() => {
    if (
      !pathname.includes("Seb") &&
      pathname !== "/privacyPolicy" &&
      pathname !== "/termsConditions"
    ) {
      if (!isLogin) {
        handleLocalStorageOnLogout();
        dispatch(onLogout());
        commonHelper.navigate("/login");
      } else {
        commonHelper.navigate("/");
      }
    }
  }, [isLogin]);

  useEffect(() => {
    const handleIframeMessage = (event) => {
      if (event?.data && (event?.data === "accessFailed" || event?.data === "Logout")) {
        // eslint-disable-next-line
        console.error("Access inside the iframe failed");
        handleLogout("Auto Logout");
      }
    };
    window.addEventListener("message", handleIframeMessage);

    return () => {
      window.removeEventListener("message", handleIframeMessage);
    };
  }, []);

  React.useEffect(() => {
    const handleBeforeUnload = async () => {
      const savedQuestion = getItem("savedQuestion");
      const isLoginTemp = getItem("isLogin");

      try {
        if (savedQuestion && isLoginTemp) {
          // Save temporer answer with delay
          autoSaveAnswer();
          for (let i = 0; i < 10000; i += 1) {
            console.log(i);
          }
        }
      } catch (error) {
        // eslint-disable-next-line
        console.error("Gagal menyimpan data:", error);
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  function ctrlShiftKey(e, keyCode) {
    return e.ctrlKey && e.shiftKey && e.keyCode === keyCode.charCodeAt(3);
  }

  const handleLimitKeboardAccess = (event) => {
    if (
      event.code === "F12" ||
      ctrlShiftKey(event, "KeyI") ||
      ctrlShiftKey(event, "KeyJ") ||
      ctrlShiftKey(event, "KeyC") ||
      (event.ctrlKey && event.code === "KeyU")
    ) {
      event.preventDefault();
      return false;
    }

    if (isLogin) {
      if (
        (event.metaKey && event.code === "KeyI") ||
        (event.metaKey && event.code === "KeyA") ||
        (event.metaKey && event.code === "KeyC") ||
        (event.metaKey && event.code === "KeyV")
      ) {
        event.preventDefault();
        return false;
      }

      if ((event.ctrlKey && event.code === "KeyC") || (event.ctrlKey && event.code === "KeyV")) {
        event.preventDefault();
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    const handleContextmenu = (e) => {
      e.preventDefault();
    };

    document.addEventListener("contextmenu", handleContextmenu);
    document.addEventListener("keydown", handleLimitKeboardAccess);

    return () => {
      document.removeEventListener("contextmenu", handleContextmenu);
      document.removeEventListener("keydown", handleLimitKeboardAccess);
    };
  }, [isLogin]);

  return (
    <ThemeProvider theme={theme}>
      <CustomProvider>
        <CssBaseline />
        <Suspense
          fallback={
            <div
              style={{
                width: "100vw",
                height: "100vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </div>
          }
        >
          <Routes>
            <Route
              path="/"
              element={
                <SebValidationWrapper key="dashboardExam">
                  <DashboardExam />
                </SebValidationWrapper>
              }
            />
            <Route
              path="/finishExam"
              element={
                <SebValidationWrapper key="finishExam">
                  <FinishExam />
                </SebValidationWrapper>
              }
            />
            <Route
              path="/instructionExam"
              element={
                <SebValidationWrapper key="instructionExam">
                  <InstructionExam />
                </SebValidationWrapper>
              }
            />
            <Route
              path="/proctoringExam"
              element={
                <SebValidationWrapper key="proctoringExam">
                  <ProctoringExam />
                </SebValidationWrapper>
              }
            />
            <Route
              path="/proctoringAmanin"
              element={
                <SebValidationWrapper key="proctoringAmanin">
                  <ProctoringAmanin />
                </SebValidationWrapper>
              }
            />
            <Route
              path="/findingPartner"
              element={
                <SebValidationWrapper key="findingPartner">
                  <FindingPartner />
                </SebValidationWrapper>
              }
            />
            <Route
              path="/questLayoutExam"
              element={
                <SebValidationWrapper key="questLayoutExam">
                  <QuestLayoutExam />
                </SebValidationWrapper>
              }
            />
            <Route
              path="/login"
              element={
                <SebValidationWrapper isSaveStatusApp key="login">
                  <SignInBasic />
                </SebValidationWrapper>
              }
            />
            <Route
              path="/termsConditions"
              element={
                <SebValidationWrapper key="termsConditions">
                  <TermsConditions />
                </SebValidationWrapper>
              }
            />
            <Route
              path="/privacyPolicy"
              element={
                <SebValidationWrapper key="privacyPolicy">
                  <PrivacyPolicy />
                </SebValidationWrapper>
              }
            />
            <Route path="/downloadSeb" element={<DownloadSeb />} />
          </Routes>
        </Suspense>
      </CustomProvider>
    </ThemeProvider>
  );
}
